/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import React, { useMemo } from 'react';
import {
  Grid,
  TextField,
  Link,
  AlertTitle,
  Alert
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { FormikErrors } from 'formik/dist/types';
import { SiteValues } from '../../models/Sites';
import { isGA4property } from '../../utils/Google';

interface Props {
  classes: any;
  site: SiteValues;
  error: SiteValues;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<FormikErrors<SiteValues>> | Promise<void>;
  handleChange: any;
  gaAccountItems: any;
  gtmAccountItems: any;
  gaWebPropertyItems: any;
  ga4WebStreamItems: any;
  gtmContainerItems: any;
  concurentAccounts: boolean;
  loading: boolean;
}

const GoogleSetup = (
  {
    loading,
    site,
    error,
    setFieldValue,
    handleChange,
    gaAccountItems,
    gaWebPropertyItems,
    ga4WebStreamItems,
    gtmAccountItems,
    gtmContainerItems,
    concurentAccounts
  }: Props
) => {
  const onGtmAccountIdChange = (e) => {
    handleChange(e);
    if (site.gtmAccountId !== e.target.value && site.gtmContainerId) {
      setTimeout(() => {
        setFieldValue('gtmContainerId', null);
      }, 200);
    }
  };

  const onAnalyticsIdChange = (e) => {
    handleChange(e);
    if (site.googleAnalyticsId !== e.target.value && site.gaWebPropertyId) {
      setTimeout(() => {
        setFieldValue('gaWebPropertyId', null);
      }, 200);
    }
  };

  const onWebPropertyIdChange = (e) => {
    handleChange(e);
    if (site.gaWebPropertyId !== e.target.value && site.gaWebStreamId) {
      setTimeout(() => {
        setFieldValue('gaWebStreamId', null);
      }, 200);
    }
  };
  
  const isGA4Property = useMemo(() => isGA4property(site?.gaWebPropertyId), [site.gaWebPropertyId]);
  
  return (
    <Grid container columnSpacing={2}>
      <Grid item xs={12}>
        <div className="centeredFlex mt-2 mb-1">
          <h4 className="coloredHeaderText m-0">Google services setup</h4>
        </div>
      </Grid>
      {
        concurentAccounts && (
          <Alert severity="error">
            <AlertTitle>No access</AlertTitle>
            You have no access to the Google Tag Manager configuration for this website. Please ask
            {' '}
            {site?.configuredByInfo?.firstname}
            {' '}
            {site?.configuredByInfo?.lastname}
            {' '}
            user with
            {' '}
            {site?.configuredByInfo?.email}
            {' '}
            email to add you to the Google Tag Manager authorised users.
            You can also set up this integration again from scratch.
          </Alert>
        )
      }

      <Grid item xs={6}>
        <TextField
          select
          fullWidth
          margin="normal"
          label="Analytics account"
          variant="standard"
          value={site.googleAnalyticsId || ''}
          onChange={onAnalyticsIdChange}
          name="googleAnalyticsId"
          error={Boolean(error.googleAnalyticsId)}
          helperText={error.googleAnalyticsId}
          disabled={loading}
        >
          {gaAccountItems}
        </TextField>
      </Grid>

      <Grid item xs={6}>
        <TextField
          select
          fullWidth
          margin="normal"
          label="Analytics web property"
          variant="standard"
          value={site.gaWebPropertyId || ''}
          onChange={onWebPropertyIdChange}
          name="gaWebPropertyId"
          error={Boolean(error.gaWebPropertyId)}
          helperText={error.gaWebPropertyId}
          disabled={loading}
        >
          {gaWebPropertyItems}
        </TextField>
      </Grid>

      {isGA4Property &&
        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Analytics web stream"
            variant="standard"
            value={site.gaWebStreamId || ''}
            onChange={handleChange}
            name="gaWebStreamId"
            error={Boolean(error.gaWebStreamId)}
            helperText={error.gaWebStreamId}
            disabled={loading}
          >
            {ga4WebStreamItems}
          </TextField>
        </Grid>
      }

      <Grid item xs={6}>
        <TextField
          select
          fullWidth
          margin="normal"
          label="Tag manager account"
          variant="standard"
          name="gtmAccountId"
          onChange={onGtmAccountIdChange}
          value={site.gtmAccountId || ''}
          error={Boolean(error.gtmAccountId)}
          helperText={error.gtmAccountId}
          disabled={loading}
        >
          {gtmAccountItems}
        </TextField>
      </Grid>

      <Grid item xs={6}>
        <TextField
          select
          fullWidth
          margin="normal"
          label="Tag manager container"
          value={site.gtmContainerId || ''}
          variant="standard"
          onChange={handleChange}
          name="gtmContainerId"
          error={Boolean(error.gtmContainerId)}
          helperText={error.gtmContainerId}
          disabled={loading}
        >
          {gtmContainerItems}
        </TextField>
      </Grid>

      <Grid item xs={6}>
        <TextField
          fullWidth
          margin="normal"
          label="Google maps API key"
          value={site.googleMapsApiKey || ''}
          variant="standard"
          onChange={handleChange}
          disabled={loading}
          name="googleMapsApiKey"
          helperText={
            site.gtmAccountId
              ? (
                <>
                  Paste the API key to activate live mapping
                  {' '}
                  <Link
                    display="inline-block"
                    sx={{
                      verticalAlign: 'sub'
                    }}
                    href="https://www.ish.com.au/onCourse/doc/manual"
                  >
                    <HelpIcon fontSize="inherit" />
                  </Link>
                  {' '}
                </>
              ) : 'Select Tag manager account first'
          }
        />
      </Grid>
    </Grid>
  );
};

export default GoogleSetup;